import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useBookmarkLoginPopup = (duration = 3000) => {
  const [visiblePopupId, setVisiblePopupId] = useState<string | null>(null);
  const { profile } = useCompanyAuth();
  const isLoggedIn = !!profile?.isLogin;
  const popupRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hidePopup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisiblePopupId(null);
  }, []);

  useClickOutside([popupRef], hidePopup, visiblePopupId !== null);

  const showPopupIfNotLoggedIn = useCallback(
    (id: string) => {
      if (isLoggedIn) {
        return false;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setVisiblePopupId(id);

      timeoutRef.current = setTimeout(() => {
        setVisiblePopupId(null);
        timeoutRef.current = null;
      }, duration);

      return true;
    },
    [isLoggedIn, duration],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    visiblePopupId,
    showPopupIfNotLoggedIn,
    hidePopup,
    popupRef,
  };
};
